import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFamilyCalendar, pushEmailToGoogleCalendar, fetchGoogleCalendarStatus } from "@/lib/api";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Mail, ClipboardCheck,
  CalendarClock, MapPin, X, Loader2, Inbox, Upload, Forward, Zap, Check,
} from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_COLOR = "#6b6051"; // var(--fcc-muted)-ish

function startOfGrid(year, monthIdx0) {
  // Returns a JS Date set to the Sunday on/before the 1st of the month.
  const first = new Date(year, monthIdx0, 1);
  const offset = first.getDay(); // 0=Sun .. 6=Sat
  const d = new Date(year, monthIdx0, 1 - offset);
  return d;
}

function isoDate(date) {
  // YYYY-MM-DD in local time (matches stored dates that are usually localized at write time).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function eventIcon(kind) {
  if (kind === "email") return Mail;
  if (kind === "task") return ClipboardCheck;
  if (kind === "event") return CalendarClock;
  return CalendarIcon;
}

export default function FamilyCalendar({ onSnapshotLoaded }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth()); // 0..11
  const [childFilter, setChildFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);

  const monthLabel = new Date(year, monthIdx, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

  useEffect(() => {
    setLoading(true);
    fetchFamilyCalendar(year, monthIdx + 1, childFilter || undefined)
      .then((d) => {
        setData(d);
        onSnapshotLoaded?.(d?.snapshot);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, monthIdx, childFilter, onSnapshotLoaded]);

  const colorByChild = useMemo(() => {
    const m = {};
    (data?.child_legend || []).forEach((c) => {
      m[c.child_id] = c.color;
    });
    return m;
  }, [data]);

  const cells = useMemo(() => {
    const start = startOfGrid(year, monthIdx);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = isoDate(d);
      const events = (data?.by_day?.[iso] || []);
      out.push({
        date: d,
        iso,
        inMonth: d.getMonth() === monthIdx,
        isToday: iso === isoDate(today),
        events,
      });
    }
    return out;
  }, [year, monthIdx, data, today]);

  const totalEvents = useMemo(
    () => Object.values(data?.by_day || {}).reduce((acc, arr) => acc + arr.length, 0),
    [data],
  );

  const prevMonth = () => {
    if (monthIdx === 0) { setMonthIdx(11); setYear(year - 1); }
    else setMonthIdx(monthIdx - 1);
  };
  const nextMonth = () => {
    if (monthIdx === 11) { setMonthIdx(0); setYear(year + 1); }
    else setMonthIdx(monthIdx + 1);
  };
  const goToday = () => { setMonthIdx(today.getMonth()); setYear(today.getFullYear()); };

  return (
    <section data-testid="family-calendar" className="fcc-card overflow-hidden">
      <header className="p-6 sm:p-7 border-b border-[var(--fcc-border)] flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center">
            <CalendarIcon className="w-4 h-4 text-[var(--fcc-green)]" />
          </span>
          <div>
            <p className="label-overline">Family calendar</p>
            <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-0.5" data-testid="calendar-month-label">{monthLabel}</h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(data?.child_legend?.length ?? 0) > 0 && (
            <div className="flex gap-1 bg-white border border-[var(--fcc-border)] rounded-xl p-1 text-sm">
              <button
                data-testid="calendar-child-all"
                onClick={() => setChildFilter("")}
                className={`px-3 py-1.5 rounded-lg ${!childFilter ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
              >
                All
              </button>
              {data.child_legend.map((c) => (
                <button
                  key={c.child_id}
                  data-testid={`calendar-child-${c.child_id}`}
                  onClick={() => setChildFilter(c.child_id)}
                  className={`px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${childFilter === c.child_id ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <button
            data-testid="calendar-prev"
            onClick={prevMonth}
            className="p-2 rounded-lg border border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)]"
            aria-label="Previous month"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            data-testid="calendar-today"
            onClick={goToday}
            className="px-3 py-1.5 rounded-lg border border-[var(--fcc-border)] text-sm font-semibold hover:bg-[var(--fcc-cream-2)]"
          >Today</button>
          <button
            data-testid="calendar-next"
            onClick={nextMonth}
            className="p-2 rounded-lg border border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)]"
            aria-label="Next month"
          ><ChevronRight className="w-4 h-4" /></button>
        </div>
      </header>

      {loading ? (
        <div className="p-16 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          <p className="text-sm mt-3">Building your family calendar…</p>
        </div>
      ) : totalEvents === 0 ? (
        <EmptyCalendarState />
      ) : (
        <>
          <div className="grid grid-cols-7 border-b border-[var(--fcc-border)] bg-[var(--fcc-cream-2)]" data-testid="calendar-weekday-header">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] text-center py-2 font-semibold">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {cells.map((c) => (
              <DayCell
                key={c.iso}
                cell={c}
                colorByChild={colorByChild}
                onClick={() => setOpenDay(c)}
              />
            ))}
          </div>
        </>
      )}

      {openDay && (
        <DayDrawer
          cell={openDay}
          colorByChild={colorByChild}
          onClose={() => setOpenDay(null)}
        />
      )}
    </section>
  );
}

function DayCell({ cell, colorByChild, onClick }) {
  const visible = cell.events.slice(0, 3);
  const more = cell.events.length - visible.length;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`calendar-day-${cell.iso}`}
      className={`text-left border-r border-b border-[var(--fcc-border)] last:border-r-0 min-h-[110px] sm:min-h-[120px] p-2 hover:bg-[var(--fcc-cream-2)] transition ${cell.inMonth ? "bg-white" : "bg-[var(--fcc-cream)]/40"}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold ${cell.isToday ? "text-white bg-[var(--fcc-green)] rounded-full w-6 h-6 inline-flex items-center justify-center" : cell.inMonth ? "text-[var(--fcc-ink)]" : "text-[var(--fcc-muted)]"}`}>
          {cell.date.getDate()}
        </span>
        {cell.events.length > 0 && (
          <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{cell.events.length}</span>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {visible.map((e, idx) => {
          const Icon = eventIcon(e.kind);
          const color = colorByChild[e.child_id] || DEFAULT_COLOR;
          return (
            <li key={idx} data-testid="calendar-event-chip" className="flex items-center gap-1 text-[11px] rounded-md px-1.5 py-1 bg-white border border-[var(--fcc-border)]">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
              <Icon className="w-3 h-3 text-[var(--fcc-muted)] shrink-0" />
              <span className="truncate" title={e.title}>{e.title}</span>
            </li>
          );
        })}
        {more > 0 && (
          <li className="text-[10px] text-[var(--fcc-muted)] pl-1">+{more} more</li>
        )}
      </ul>
    </button>
  );
}

function DayDrawer({ cell, colorByChild, onClose }) {
  const [gcalStatus, setGcalStatus] = useState(null);
  const [pushing, setPushing] = useState({});
  const [pushed, setPushed] = useState({});

  useEffect(() => {
    fetchGoogleCalendarStatus().then(setGcalStatus).catch(() => setGcalStatus(null));
  }, []);

  const approveToCalendar = async (event) => {
    if (!event.id) return;
    setPushing((p) => ({ ...p, [event.id]: true }));
    try {
      const r = await pushEmailToGoogleCalendar(event.id);
      setPushed((p) => ({ ...p, [event.id]: r.html_link || true }));
      toast.success("Added to Google Calendar.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "scope_missing") {
        toast.error("Reconnect Google to enable Calendar.", { description: detail.hint });
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't add to Google Calendar.");
      }
    } finally {
      setPushing((p) => ({ ...p, [event.id]: false }));
    }
  };

  // Group events by child for the per-child Day Summary
  const byChild = useMemo(() => {
    const m = new Map();
    cell.events.forEach((e) => {
      const key = e.child_id || "_family";
      if (!m.has(key)) m.set(key, { name: e.child_name || "Family", color: colorByChild[e.child_id] || DEFAULT_COLOR, items: [] });
      m.get(key).items.push(e);
    });
    return Array.from(m.values());
  }, [cell, colorByChild]);

  const dayLabel = cell.date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="day-drawer">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 bg-[var(--fcc-ink)]/40 backdrop-blur-sm"
      />
      <aside className="w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="p-6 sm:p-7 border-b border-[var(--fcc-border)] flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
          <div>
            <p className="label-overline">Day summary</p>
            <h3 className="font-display text-2xl text-[var(--fcc-ink)] mt-1" data-testid="day-drawer-title">{dayLabel}</h3>
            <p className="text-xs text-[var(--fcc-muted)] mt-1">{cell.events.length} item{cell.events.length === 1 ? "" : "s"} across the family</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl" data-testid="day-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-6 sm:p-7 space-y-7">
          {cell.events.length === 0 ? (
            <p className="text-sm text-[var(--fcc-muted)]" data-testid="day-drawer-empty">No family activity found for this day.</p>
          ) : (
            byChild.map((group, gi) => (
              <section key={gi} data-testid="day-drawer-child-group">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: group.color }} />
                  <h4 className="font-display text-lg text-[var(--fcc-ink)]">{group.name}</h4>
                  <span className="text-xs text-[var(--fcc-muted)] ml-auto">{group.items.length}</span>
                </div>
                <ul className="mt-3 space-y-2.5">
                  {group.items.map((e, idx) => {
                    const Icon = eventIcon(e.kind);
                    const when = e.when ? new Date(e.when) : null;
                    const timeStr = when && when.getHours() + when.getMinutes() > 0
                      ? when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                      : null;
                    return (
                      <li key={idx} data-testid="day-drawer-event" className="rounded-xl border border-[var(--fcc-border)] p-3.5">
                        <div className="flex items-start gap-2.5">
                          <span className="w-7 h-7 rounded-lg bg-[var(--fcc-cream-2)] flex items-center justify-center shrink-0">
                            <Icon className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-[var(--fcc-ink)] leading-snug">{e.title}</p>
                            {e.summary && e.summary !== e.title && (
                              <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">{e.summary}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {timeStr && (
                                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{timeStr}</span>
                              )}
                              {e.category && (
                                <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)] text-[10px]">{e.category}</span>
                              )}
                              {e.priority && (
                                <span className="fcc-pill text-[10px]" style={{ background: "var(--fcc-peach)", color: "var(--fcc-ink)" }}>{e.priority}</span>
                              )}
                              <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">source · {e.source || e.kind}</span>
                              {e.is_deadline && (
                                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-orange,#b3530b)]">deadline</span>
                              )}
                              {e.location && (
                                <span className="text-[10px] text-[var(--fcc-muted)] inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {e.location}</span>
                              )}
                              {e.completed && (
                                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-green)]">done</span>
                              )}
                            </div>
                            {e.kind === "email" && gcalStatus?.can_write && (
                              <div className="mt-2.5">
                                {pushed[e.id] ? (
                                  <a
                                    href={typeof pushed[e.id] === "string" ? pushed[e.id] : "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`approve-calendar-done-${e.id}`}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--fcc-green)]"
                                  >
                                    <Check className="w-3 h-3" /> Added to Google Calendar
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => approveToCalendar(e)}
                                    disabled={!!pushing[e.id]}
                                    data-testid={`approve-calendar-btn-${e.id}`}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[var(--fcc-ink)] text-white hover:opacity-90"
                                  >
                                    <Check className="w-3 h-3" />
                                    {pushing[e.id] ? "Adding…" : "Approve to Calendar"}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}

function EmptyCalendarState() {
  return (
    <div className="p-10 sm:p-14 text-center" data-testid="calendar-empty">
      <Inbox className="w-8 h-8 text-[var(--fcc-muted)] mx-auto" />
      <h3 className="font-display text-xl text-[var(--fcc-ink)] mt-4">Your family calendar is quiet</h3>
      <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-md mx-auto">
        Connect Gmail or upload emails to automatically build your family calendar.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Link to="/app/gmail" data-testid="calendar-empty-gmail" className="fcc-btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
          <Zap className="w-4 h-4" /> Connect Gmail
        </Link>
        <Link to="/app/gmail" data-testid="calendar-empty-upload" className="px-4 py-2 rounded-xl text-sm font-semibold border border-[var(--fcc-border)] inline-flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload Email
        </Link>
        <Link to="/app/gmail" data-testid="calendar-empty-forward" className="px-4 py-2 rounded-xl text-sm font-semibold border border-[var(--fcc-border)] inline-flex items-center gap-2">
          <Forward className="w-4 h-4" /> Forward Email
        </Link>
      </div>
    </div>
  );
}
